import React, { useEffect, useState, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Tooltip, Box } from '@mui/material';
import noUserPhoto from "../../images/no_photo_person.png";
import { getBaseApiUrl, getUploadUrl } from '../../service/ServiceApi';
import { useAuthentication } from '../../reducer/authentication';
import { getExperienceNeedleByLevel } from '../../utils';

interface UserPhotoProps {
    width?: number;
    height?: number;
    className?: string;
    id: number;
    photo: string | null;
    style?: React.CSSProperties;
    /** Se o usuário da foto usa LanzaCoin */
    useLanzaCoin?: boolean;
    /** Experiência do usuário da foto */
    experience?: number;
}

// Badge de nível
const LevelBadge = styled('span')<{ $bgColor: string; $textColor: string }>(({ $bgColor, $textColor }) => ({
    position: 'absolute',
    bottom: 0,
    right: 0,
    background: $bgColor,
    color: $textColor,
    fontSize: 9,
    fontWeight: 700,
    padding: '1px 4px',
    borderRadius: 6,
    minWidth: 16,
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.4)',
    zIndex: 1,
    lineHeight: 1.3,
}));

/**
 * Retorna cores do badge baseadas no nível
 */
function getLevelStyle(level: number) {
    if (level >= 40) {
        return { badge: 'linear-gradient(135deg, #FFD700, #FFA500)', text: '#000' };
    }
    if (level >= 30) {
        return { badge: 'linear-gradient(135deg, #9B59B6, #8E44AD)', text: '#fff' };
    }
    if (level >= 20) {
        return { badge: 'linear-gradient(135deg, #3498DB, #2980B9)', text: '#fff' };
    }
    if (level >= 10) {
        return { badge: 'linear-gradient(135deg, #2ECC71, #27AE60)', text: '#fff' };
    }
    return { badge: 'linear-gradient(135deg, #95a5a6, #7f8c8d)', text: '#fff' };
}

/**
 * Calcula o nível do usuário baseado na experiência total
 */
function calculateLevel(experience: number): number {
    const experienceMap = getExperienceNeedleByLevel();
    let remainingXp = experience;

    for (let level = 1; level <= 50; level++) {
        const requirement = experienceMap[level];

        if (requirement === null) {
            return 50;
        }

        if (remainingXp >= requirement) {
            remainingXp -= requirement;
        } else {
            return level;
        }
    }

    return 50;
}

export default function UserPhoto(props: UserPhotoProps) {
    const { height, width, className, id, photo, style, useLanzaCoin, experience } = props;
    const photoPath = photo ? getUploadUrl() + `User/${id}/${photo}` : noUserPhoto;

    const [url, setUrl] = useState<string>(photoPath);
    const { user: loggedUser } = useAuthentication();

    // Calcula o nível do usuário da foto
    const userLevel = useMemo(() => {
        if (experience !== undefined && experience >= 0) {
            return calculateLevel(experience);
        }
        return 0;
    }, [experience]);

    // Verifica se ambos usam LanzaCoin (para mostrar o badge de nível)
    const showLevelBadge = useMemo(() => {
        const loggedUserHasLanzaCoin = loggedUser?.use_lanza_coin === true;
        const photoUserHasLanzaCoin = useLanzaCoin === true;
        return loggedUserHasLanzaCoin && photoUserHasLanzaCoin && userLevel >= 1;
    }, [loggedUser?.use_lanza_coin, useLanzaCoin, userLevel]);

    // Cores do badge baseadas no nível
    const levelStyle = getLevelStyle(userLevel);

    useEffect(() => {
        setUrl(photoPath);
    }, [photoPath]);

    function onError() {
        setUrl(noUserPhoto);
    }

    const imgElement = (
        <img 
            className={className} 
            id={`userPhoto`} 
            height={height} 
            width={width} 
            src={url} 
            onError={onError} 
            style={style} 
        />
    );

    // Mostra badge de nível quando ambos usam LanzaCoin
    if (showLevelBadge) {
        const tooltipText = `Nível ${userLevel}`;
        return (
            <Tooltip title={tooltipText} placement="top" arrow>
                <Box 
                    component="span" 
                    sx={{ 
                        position: 'relative', 
                        display: 'flex'
                    }}
                >
                    {imgElement}
                    <LevelBadge 
                        $bgColor={levelStyle.badge} 
                        $textColor={levelStyle.text}
                    >
                        {userLevel}
                    </LevelBadge>
                </Box>
            </Tooltip>
        );
    }
    
    return imgElement;
}